import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, generatePath } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AttentionProvider } from "@/contexts/AttentionContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteTitle from "@/components/RouteTitle";

// Eager — first-paint critical (landing, auth)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Forbidden from "@/pages/Forbidden";
import PWAInstallBanner from "@/components/PWAInstallBanner";

// Lazy — split bundles, loaded on demand
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const BrandDashboard = lazy(() => import("@/pages/brand/BrandDashboard"));
const BrandFinancial = lazy(() => import("@/pages/brand/BrandFinancial"));
const SessionsPage = lazy(() => import("@/pages/settings/SessionsPage"));
const ProfilePage = lazy(() => import("@/pages/settings/ProfilePage"));
const AdminTeamPage = lazy(() => import("@/pages/admin/AdminTeamPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminEmailsPage = lazy(() => import("@/pages/admin/AdminEmailsPage"));
const Discover = lazy(() => import("@/pages/brand/Discover"));
const NewCampaign = lazy(() => import("@/pages/brand/NewCampaign"));
const BrandContracts = lazy(() => import("@/pages/brand/BrandContracts"));
const CampaignApplications = lazy(() => import("@/pages/brand/CampaignApplications"));

const CreatorDashboard = lazy(() => import("@/pages/creator/CreatorDashboard"));
const CreatorProfile = lazy(() => import("@/pages/creator/CreatorProfile"));
const CreatorContracts = lazy(() => import("@/pages/creator/CreatorContracts"));
const CreatorFinancial = lazy(() => import("@/pages/creator/CreatorFinancial"));
const CreatorVerification = lazy(() => import("@/pages/creator/CreatorVerification"));
const CreatorBankAccounts = lazy(() => import("@/pages/creator/CreatorBankAccounts"));
const CreatorPixKeys = lazy(() => import("@/pages/creator/CreatorPixKeys"));
const CreatorAddress = lazy(() => import("@/pages/creator/CreatorAddress"));
const MyApplications = lazy(() => import("@/pages/creator/MyApplications"));
const MyInvitations = lazy(() => import("@/pages/creator/MyInvitations"));

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminOperations = lazy(() => import("@/pages/admin/AdminOperations"));
const AdminUserList = lazy(() => import("@/pages/admin/AdminUserList"));
const AdminContracts = lazy(() => import("@/pages/admin/AdminContracts"));
const AdminAudit = lazy(() => import("@/pages/admin/AdminAudit"));
const AdminFunnel = lazy(() => import("@/pages/admin/AdminFunnel"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminSystemLogs = lazy(() => import("@/pages/admin/AdminSystemLogs"));
const AdminIntegrations = lazy(() => import("@/pages/admin/AdminIntegrations"));
const AdminKYC = lazy(() => import("@/pages/admin/AdminKYC"));

const PublicCreator = lazy(() => import("@/pages/PublicCreator"));
const HowMatchWorks = lazy(() => import("@/pages/HowMatchWorks"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const CampaignBriefing = lazy(() => import("@/pages/CampaignBriefing"));
const ContractDetail = lazy(() => import("@/pages/ContractDetail"));
const VerifyCertificate = lazy(() => import("@/pages/VerifyCertificate"));
const EmailSent = lazy(() => import("@/pages/EmailSent"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]" data-testid="route-fallback">
      <div className="w-8 h-8 rounded-full border-2 border-[#E8E6E1] border-t-[#DC2626] animate-spin" />
    </div>
  );
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.user_type === "brand") return <Navigate to="/painel/marca" replace />;
  if (user.user_type === "creator") return <Navigate to="/painel/criador" replace />;
  if (user.user_type === "admin") return <Navigate to="/painel/admin" replace />;
  return <Navigate to="/" replace />;
}

/**
 * Redirect helper that interpolates dynamic URL params.
 * Solves the bug: <Navigate to="/certificado/:contractId" /> would navigate literally
 * to ':contractId'. This component uses generatePath to substitute the current match's params.
 */
function ParamRedirect({ template }) {
  const params = useParams();
  return <Navigate to={generatePath(template, params)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AttentionProvider>
            <RouteTitle />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ============================================================
                  PUBLIC — routes in Portuguese
                  ============================================================ */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Register />} />
              <Route path="/cadastro/marca" element={<Navigate to="/cadastro?role=brand" replace />} />
              <Route path="/cadastro/criador" element={<Navigate to="/cadastro?role=creator" replace />} />
              <Route path="/esqueci-senha" element={<ForgotPassword />} />
              <Route path="/redefinir-senha" element={<ResetPassword />} />
              <Route path="/403" element={<Forbidden />} />
              <Route path="/certificado/:contractId" element={<VerifyCertificate />} />
              <Route path="/confirmar-email/:token" element={<VerifyEmail />} />
              <Route path="/email-enviado" element={<EmailSent />} />
              <Route path="/termos" element={<Terms />} />
              <Route path="/privacidade" element={<Privacy />} />
              <Route path="/criador/:creatorId" element={<PublicCreator />} />
              <Route path="/como-funciona" element={<HowItWorks />} />
              <Route path="/como-funciona/match" element={<HowMatchWorks />} />
              <Route path="/campanhas/:campaignId" element={<ProtectedRoute roles={["creator","brand"]}><CampaignBriefing /></ProtectedRoute>} />

              <Route path="/painel" element={<DashboardRedirect />} />

              {/* Brand — PT */}
              <Route path="/painel/marca" element={<ProtectedRoute roles={["brand"]}><BrandDashboard /></ProtectedRoute>} />
              <Route path="/painel/marca/descobrir" element={<ProtectedRoute roles={["brand"]}><Discover /></ProtectedRoute>} />
              <Route path="/painel/marca/campanhas/nova" element={<ProtectedRoute roles={["brand"]}><NewCampaign /></ProtectedRoute>} />
              <Route path="/painel/marca/contratos" element={<ProtectedRoute roles={["brand"]}><BrandContracts /></ProtectedRoute>} />
              <Route path="/painel/marca/contratos/:id" element={<ProtectedRoute roles={["brand"]}><ContractDetail role="brand" /></ProtectedRoute>} />
              <Route path="/painel/marca/financeiro" element={<ProtectedRoute roles={["brand"]}><BrandFinancial /></ProtectedRoute>} />
              <Route path="/painel/marca/campanhas/:campaignId/candidaturas" element={<ProtectedRoute roles={["brand"]}><CampaignApplications /></ProtectedRoute>} />

              {/* Creator — PT */}
              <Route path="/painel/criador" element={<ProtectedRoute roles={["creator"]}><CreatorDashboard /></ProtectedRoute>} />
              <Route path="/painel/criador/perfil" element={<ProtectedRoute roles={["creator"]}><CreatorProfile /></ProtectedRoute>} />
              <Route path="/painel/criador/candidaturas" element={<ProtectedRoute roles={["creator"]}><MyApplications /></ProtectedRoute>} />
              <Route path="/painel/criador/convites" element={<ProtectedRoute roles={["creator"]}><MyInvitations /></ProtectedRoute>} />
              <Route path="/painel/criador/contratos" element={<ProtectedRoute roles={["creator"]}><CreatorContracts /></ProtectedRoute>} />
              <Route path="/painel/criador/contratos/:id" element={<ProtectedRoute roles={["creator"]}><ContractDetail role="creator" /></ProtectedRoute>} />
              <Route path="/painel/criador/financeiro" element={<ProtectedRoute roles={["creator"]}><CreatorFinancial /></ProtectedRoute>} />
              <Route path="/painel/criador/verificacao" element={<ProtectedRoute roles={["creator"]}><CreatorVerification /></ProtectedRoute>} />
              <Route path="/painel/criador/contas-bancarias" element={<ProtectedRoute roles={["creator"]}><CreatorBankAccounts /></ProtectedRoute>} />
              <Route path="/painel/criador/chaves-pix" element={<ProtectedRoute roles={["creator"]}><CreatorPixKeys /></ProtectedRoute>} />
              <Route path="/painel/criador/endereco" element={<ProtectedRoute roles={["creator"]}><CreatorAddress /></ProtectedRoute>} />

              {/* Admin — PT */}
              <Route path="/painel/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/painel/admin/operacoes" element={<ProtectedRoute roles={["admin"]}><AdminOperations /></ProtectedRoute>} />
              <Route path="/painel/admin/marcas" element={<ProtectedRoute roles={["admin"]}><AdminUserList userType="brand" /></ProtectedRoute>} />
              <Route path="/painel/admin/criadores" element={<ProtectedRoute roles={["admin"]}><AdminUserList userType="creator" /></ProtectedRoute>} />
              <Route path="/painel/admin/contratos" element={<ProtectedRoute roles={["admin"]}><AdminContracts /></ProtectedRoute>} />
              <Route path="/painel/admin/auditoria" element={<ProtectedRoute roles={["admin"]}><AdminAudit /></ProtectedRoute>} />
              <Route path="/painel/admin/funil" element={<ProtectedRoute roles={["admin"]}><AdminFunnel /></ProtectedRoute>} />
              <Route path="/painel/admin/financeiro" element={<ProtectedRoute roles={["admin"]}><AdminFinance /></ProtectedRoute>} />
              <Route path="/painel/admin/logs-sistema" element={<ProtectedRoute roles={["admin"]}><AdminSystemLogs /></ProtectedRoute>} />
              <Route path="/painel/admin/integracoes" element={<ProtectedRoute roles={["admin"]}><AdminIntegrations /></ProtectedRoute>} />
              <Route path="/painel/admin/kyc" element={<ProtectedRoute roles={["admin"]}><AdminKYC /></ProtectedRoute>} />
              <Route path="/painel/admin/equipe" element={<ProtectedRoute roles={["admin"]}><AdminTeamPage /></ProtectedRoute>} />
              <Route path="/painel/admin/configuracoes" element={<ProtectedRoute roles={["admin"]}><AdminSettingsPage /></ProtectedRoute>} />
              <Route path="/painel/admin/emails" element={<ProtectedRoute roles={["admin"]}><AdminEmailsPage /></ProtectedRoute>} />

              <Route path="/configuracoes/sessoes" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
              <Route path="/configuracoes/perfil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

              {/* ============================================================
                  LEGACY EN routes — permanent redirects (bookmarks safe)
                  ============================================================ */}
              <Route path="/register" element={<Navigate to="/cadastro" replace />} />
              <Route path="/verify/:contractId" element={<ParamRedirect template="/certificado/:contractId" />} />
              <Route path="/verify-email/:token" element={<ParamRedirect template="/confirmar-email/:token" />} />
              <Route path="/email-sent" element={<Navigate to="/email-enviado" replace />} />
              <Route path="/terms" element={<Navigate to="/termos" replace />} />
              <Route path="/privacy" element={<Navigate to="/privacidade" replace />} />
              <Route path="/c/:creatorId" element={<ParamRedirect template="/criador/:creatorId" />} />
              <Route path="/campaigns/:campaignId" element={<ParamRedirect template="/campanhas/:campaignId" />} />

              <Route path="/dashboard" element={<Navigate to="/painel" replace />} />
              <Route path="/dashboard/brand" element={<Navigate to="/painel/marca" replace />} />
              <Route path="/dashboard/brand/discover" element={<Navigate to="/painel/marca/descobrir" replace />} />
              <Route path="/dashboard/brand/campaigns/new" element={<Navigate to="/painel/marca/campanhas/nova" replace />} />
              <Route path="/dashboard/brand/contracts" element={<Navigate to="/painel/marca/contratos" replace />} />
              <Route path="/dashboard/brand/contracts/:id" element={<ParamRedirect template="/painel/marca/contratos/:id" />} />
              <Route path="/dashboard/brand/financial" element={<Navigate to="/painel/marca/financeiro" replace />} />
              <Route path="/dashboard/brand/campaigns/:campaignId/applications" element={<ParamRedirect template="/painel/marca/campanhas/:campaignId/candidaturas" />} />

              <Route path="/dashboard/creator" element={<Navigate to="/painel/criador" replace />} />
              <Route path="/dashboard/creator/profile" element={<Navigate to="/painel/criador/perfil" replace />} />
              <Route path="/dashboard/creator/applications" element={<Navigate to="/painel/criador/candidaturas" replace />} />
              <Route path="/dashboard/creator/invitations" element={<Navigate to="/painel/criador/convites" replace />} />
              <Route path="/dashboard/creator/contracts" element={<Navigate to="/painel/criador/contratos" replace />} />
              <Route path="/dashboard/creator/contracts/:id" element={<ParamRedirect template="/painel/criador/contratos/:id" />} />
              <Route path="/dashboard/creator/financial" element={<Navigate to="/painel/criador/financeiro" replace />} />

              <Route path="/dashboard/admin" element={<Navigate to="/painel/admin" replace />} />
              <Route path="/dashboard/admin/operations" element={<Navigate to="/painel/admin/operacoes" replace />} />
              <Route path="/dashboard/admin/brands" element={<Navigate to="/painel/admin/marcas" replace />} />
              <Route path="/dashboard/admin/creators" element={<Navigate to="/painel/admin/criadores" replace />} />
              <Route path="/dashboard/admin/contracts" element={<Navigate to="/painel/admin/contratos" replace />} />
              <Route path="/dashboard/admin/audit" element={<Navigate to="/painel/admin/auditoria" replace />} />
              <Route path="/dashboard/admin/funnel" element={<Navigate to="/painel/admin/funil" replace />} />
              <Route path="/dashboard/admin/finance" element={<Navigate to="/painel/admin/financeiro" replace />} />
              <Route path="/dashboard/admin/system-logs" element={<Navigate to="/painel/admin/logs-sistema" replace />} />
              <Route path="/dashboard/admin/integrations" element={<Navigate to="/painel/admin/integracoes" replace />} />
              <Route path="/dashboard/admin/team" element={<Navigate to="/painel/admin/equipe" replace />} />
              <Route path="/dashboard/admin/settings" element={<Navigate to="/painel/admin/configuracoes" replace />} />
              <Route path="/settings/sessions" element={<Navigate to="/configuracoes/sessoes" replace />} />

              {/* Catch-all → dedicated 404 (not silent redirect) */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </AttentionProvider>
          <PWAInstallBanner />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
